import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { ArrowRight, Check, Mic, BarChart3, Calendar, Sparkles } from 'lucide-react';

const ARIAONE_LOGO = "/ariaone_logo_black.png";
const LUMINA_LOGO = "https://customer-assets.emergentagent.com/job_revenue-cycle-1/artifacts/rms1wglh_lumina_logo_black.png";
const REVQ_LOGO = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/zakm44og_revQ_logo_black.png";
const ECHO_LOGO = "https://customer-assets.emergentagent.com/job_medcycle-metrics/artifacts/r0ngtnwn_echo_logo_black.svg";

const APPS = [
  {
    key: 'echo', logo: ECHO_LOGO, name: 'Echo', tag: 'Front Office Operations',
    desc: 'Intelligent scheduling, patient flow tracking, and AI phone agents.',
    icon: Calendar, accent: 'border-blue-300 bg-blue-50', checkColor: 'bg-blue-600',
    features: ['Smart Scheduling', 'Patient Flow', 'AI Phone Agents', 'Document Lens'],
  },
  {
    key: 'lumina', logo: LUMINA_LOGO, name: 'Lumina', tag: 'Clinical Intelligence',
    desc: 'AI-powered clinical documentation with ambient listening and smart SOAP notes.',
    icon: Mic, accent: 'border-slate-300 bg-slate-50', checkColor: 'bg-slate-900',
    features: ['Ambient Listening', 'Auto SOAP Notes', 'AI Coding', 'Lumina Lens'],
  },
  {
    key: 'revq', logo: REVQ_LOGO, name: 'RevQ', tag: 'Billing Intelligence',
    desc: 'Revenue cycle management with claims scrubbing, denial management, and forecasting.',
    icon: BarChart3, accent: 'border-emerald-300 bg-emerald-50', checkColor: 'bg-emerald-600',
    features: ['Claims Scrubbing', 'Denial Management', 'Revenue Analytics', 'Forecasting'],
  },
];

const AppSelectorPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preSelect = params.get('app');
  const [selected, setSelected] = useState(new Set(preSelect ? [preSelect] : []));

  useEffect(() => {
    if (preSelect && !selected.has(preSelect)) setSelected(new Set([preSelect]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelect]);

  const toggle = (key) => {
    const next = new Set(selected);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelected(next);
  };

  const handleContinue = () => {
    navigate('/setup', { state: { apps: [...selected] } });
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4" data-testid="app-selector-page">
      <div className="w-full max-w-[680px] space-y-8">
        {/* Logo */}
        <div className="text-center">
          <img src={ARIAONE_LOGO} alt="AriaOne" className="h-8 mx-auto mb-2" data-testid="selector-logo" />
          <p className="text-xs text-slate-400 tracking-wider font-medium">HEALTHCARE PLATFORM</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold text-slate-900" data-testid="selector-title">Choose your applications</h1>
            <p className="text-sm text-slate-500 mt-1">Select the modules you'd like to set up. You can add more later.</p>
          </div>

          <div className="space-y-3 mb-6">
            {APPS.map(app => {
              const isSelected = selected.has(app.key);
              return (
                <button key={app.key} onClick={() => toggle(app.key)}
                  className={cn("w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all",
                    isSelected ? app.accent : "border-slate-200 hover:border-slate-300 bg-white")}
                  data-testid={`app-card-${app.key}`}>
                  <div className={cn("h-5 w-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all",
                    isSelected ? `${app.checkColor} border-transparent` : "border-slate-300")}>
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <img src={app.logo} alt={app.name} className="h-5 w-auto" />
                      <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-500">{app.tag}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{app.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {app.features.map(f => (
                        <span key={f} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{f}</span>
                      ))}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selected.size >= 2 && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2" data-testid="bundle-callout">
              <Sparkles className="h-4 w-4 text-slate-700" />
              <p className="text-xs text-slate-600"><span className="font-semibold">Bundle discount:</span> Save 20% when you subscribe to {selected.size}+ applications.</p>
            </div>
          )}

          <Button onClick={handleContinue} disabled={selected.size === 0}
            className={cn("w-full h-11 text-sm font-medium transition-all",
              selected.size > 0 ? "bg-slate-900 hover:bg-slate-800" : "bg-slate-200 text-slate-400 cursor-not-allowed")}
            data-testid="selector-continue-btn">
            Set up {selected.size > 0 ? `${selected.size} application${selected.size > 1 ? 's' : ''}` : 'selected apps'} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppSelectorPage;
